import React from "react"
import { useTranslation } from "react-i18next"

import { CombinedVolumePercent } from "@/components/combined-volume-percent"
import { useUnit } from "@/components/unit/use-unit"
import { useFormatDecimal } from "@/formatters"
import { UnitName } from "@/models/unit"
import { DataGridColumnHeader, baseNumberColumnDef } from "@/nzds/data-grid"
import { HoverPopover } from "@/nzds/feedback/hover-popover"
import { Blur } from "@/nzds/ui-effects/blur"

import { Stack, Typography } from "@mui/material"
import type {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-pro"

type CommonSiteDataGridColumnName = "combinedEnergyUsage"

interface CommonSiteDataGridRow extends GridValidRowModel {
  annual_volume_electricity?: number | null
  annual_volume_nat_gas?: number | null
  combined_volume?: number | null
  id?: number | string
}

const calculateIsBlurred = (rowId: number | string | undefined): boolean => {
  return Number(rowId) < 0
}

interface UseCommonSiteDataGridColumnsProps {
  isBlurEnabled?: boolean
}

export const useCommonSiteDataGridColumns = <
  TRow extends CommonSiteDataGridRow,
>(
  { isBlurEnabled }: UseCommonSiteDataGridColumnsProps = {
    isBlurEnabled: false,
  }
): Record<CommonSiteDataGridColumnName, GridColDef<TRow>> => {
  const { t } = useTranslation()

  const { formatDecimal: formatDecimalToInteger } = useFormatDecimal({
    precision: 0,
  })

  const { formatDecimal: formatCombinedEnergyUsage } = useFormatDecimal(
    {
      precision: 0,
    },
    {
      showUnit: false,
      unit: UnitName.KiloBritishThermalUnit,
    }
  )

  const kbtuUnit = useUnit(UnitName.KiloBritishThermalUnit)
  const kwhUnit = useUnit(UnitName.KilowattHour)
  const thermUnit = useUnit(UnitName.Therm)

  const columns = React.useMemo(() => {
    return {
      combinedEnergyUsage: {
        ...baseNumberColumnDef,
        field: "combined_volume",
        headerName: t("gridColumn.combinedEnergyUsage.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<TRow>) => (
          <Blur
            isBlurred={
              isBlurEnabled ? calculateIsBlurred(params.row.id) : false
            }
          >
            <HoverPopover
              content={
                <Stack
                  sx={{
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    {t("capitalizeFirst", {
                      value: t("electricity", { ns: "common" }),
                    })}
                    :{" "}
                    {formatDecimalToInteger(
                      params.row.annual_volume_electricity ?? 0
                    )}{" "}
                    {kwhUnit}{" "}
                    <CombinedVolumePercent
                      total={params.row.combined_volume}
                      unit={UnitName.KilowattHour}
                      value={params.row.annual_volume_electricity}
                    />
                  </Typography>
                  <Typography variant="body2">
                    {t("capitalizeFirst", {
                      value: t("naturalGas", { ns: "common" }),
                    })}
                    :{" "}
                    {formatDecimalToInteger(
                      params.row.annual_volume_nat_gas ?? 0
                    )}{" "}
                    {thermUnit}{" "}
                    <CombinedVolumePercent
                      total={params.row.combined_volume}
                      unit={UnitName.Therm}
                      value={params.row.annual_volume_nat_gas}
                    />
                  </Typography>
                </Stack>
              }
            >
              <span>
                {formatCombinedEnergyUsage(params.row.combined_volume)}
              </span>
            </HoverPopover>
          </Blur>
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="combined_volume"
            headerName={t("gridColumn.combinedEnergyUsage.name", {
              ns: "common",
            })}
            tooltipContent={t("gridColumn.combinedEnergyUsage.description", {
              ns: "common",
            })}
            unit={kbtuUnit}
          />
        ),
        width: 220,
      },
    }
  }, [
    formatCombinedEnergyUsage,
    formatDecimalToInteger,
    isBlurEnabled,
    kbtuUnit,
    kwhUnit,
    thermUnit,
    t,
  ])

  return columns
}
